import React from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';

interface UsageCellProps {
  current: number;
  limit: number | null; // null = unlimited
  periodStart?: string;
  periodEnd?: string;
  isLifetime?: boolean; // Starter plan uses lifetime limits
  isOverridden?: boolean; // Admin has overridden this limit
}

const UsageCell: React.FC<UsageCellProps> = ({
  current,
  limit,
  periodStart,
  periodEnd,
  isLifetime = false,
  isOverridden = false,
}) => {
  // Calculate percentage for color coding
  const getColor = (): string => {
    if (limit === null) return '#6c757d'; // Gray for unlimited
    const percentage = (current / limit) * 100;
    if (percentage >= 100) return '#dc3545'; // Red - at limit
    if (percentage >= 80) return '#ffc107'; // Yellow - warning
    return '#6c757d'; // Gray - normal
  };

  const formatDisplay = (): string => {
    if (limit === null) {
      return `${current.toLocaleString()} / ∞`;
    }
    return `${current.toLocaleString()} / ${limit.toLocaleString()}`;
  };

  const getTooltipContent = (): string => {
    let content = '';

    if (isOverridden) {
      content += 'Admin override\n';
    }

    if (limit === null) {
      content += 'Unlimited';
      return content;
    }
    const percentage = Math.round((current / limit) * 100);
    const remaining = Math.max(0, limit - current);

    content += `${percentage}% used (${remaining.toLocaleString()} remaining)`;

    if (!isLifetime && periodStart && periodEnd) {
      const start = new Date(periodStart).toLocaleDateString();
      const end = new Date(periodEnd).toLocaleDateString();
      content += `\nBilling period: ${start} - ${end}`;
    } else if (isLifetime) {
      content += '\nLifetime limit';
    }

    return content;
  };

  const tooltip = (
    <Tooltip id="usage-tooltip" style={{ whiteSpace: 'pre-line' }}>
      {getTooltipContent()}
    </Tooltip>
  );

  return (
    <OverlayTrigger placement="top" overlay={tooltip}>
      <span
        style={{
          color: getColor(),
          fontWeight: limit !== null && current >= limit ? 'bold' : 'normal',
          cursor: 'help',
          fontSize: '0.875rem',
        }}
      >
        {formatDisplay()}
        {isOverridden && (
          <span
            style={{
              marginLeft: '4px',
              color: '#0d6efd',
              fontSize: '0.7rem',
              fontWeight: 'bold',
            }}
            title="Admin override"
          >
            *
          </span>
        )}
      </span>
    </OverlayTrigger>
  );
};

export default UsageCell;
